// Panel de edición de video con IA (Gemini Omni Flash).
//
// Feature NUEVA e independiente del editor de Sora (`editar_vid`/`EditarVideo`):
// NO lo reemplaza. Se abre en la SIDEBAR izquierda del editor (como "Nueva
// escena con IA"), NO como modal. Layout tipo chat: el VIDEO (versión actual /
// editada) arriba llenando el espacio, y el COMPOSER (prompt + botones) abajo.
// Corre el flujo async de `omniEditVideo`. Solo clips ≤10s. Ver ADR-500.
//
// El componente NO persiste: al Guardar llama `onSave(result, mode)` y el
// contenedor (el editor) decide qué hacer (reemplazar el clip vs sumar asset).
// El header/cierre del panel los provee el contenedor del editor.
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Sparkles,
  Undo2,
  Redo2,
  Save,
  Loader2,
  Check,
  ArrowUp,
  X,
  Info,
  ChevronDown,
  Copy,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import {
  omniEditVideo,
  creditsForOmniEdit,
  type OmniAspectRatio,
} from '../lib/omniEdit';

export interface EditarVideoIASaveResult {
  /** Signed URL del video editado elegido. */
  url: string;
  /** Storage path del video editado en `video-project-assets`. */
  path: string;
}

export interface EditarVideoIAProps {
  /** Signed URL (descargable) del clip original a editar. */
  clipUrl: string;
  /** Storage path del asset original (para "Guardar" = reemplazar). Opcional. */
  originalPath?: string;
  /** Duración del clip en segundos (≤10). Define el costo en créditos. */
  durationSec: number;
  aspectRatio: OmniAspectRatio;
  projectId: string;
  companyId: string;
  /**
   * Se llama al Guardar. `mode`:
   *  - 'replace' → reemplazar el clip original por el editado.
   *  - 'copy'    → conservar el original y sumar el editado como nuevo.
   */
  onSave: (
    result: EditarVideoIASaveResult,
    mode: 'replace' | 'copy',
  ) => void | Promise<void>;
  /** Cierra el panel (el contenedor lo mapea a setActivePanel('none')). */
  onClose: () => void;
  /**
   * Si es `true`, muestra el video ORIGINAL desde el arranque (como el flujo de
   * "editar imagen"). Si es `false` (default, timeline), arriba queda un
   * placeholder en blanco hasta que se genera la primera edición.
   */
  showOriginalPreview?: boolean;
  /** Texto secundario del ítem "Guardar" del menú (según el contexto). */
  replaceHint?: string;
  /** Texto secundario del ítem "Guardar como copia" del menú. */
  copyHint?: string;
  /**
   * Si es `true`, muestra UN SOLO botón "Guardar" (sin el split "Guardar como
   * copia" / reemplazar). Usado en el historial, donde NUNCA se reemplaza ni
   * borra el video original: siempre se guarda como item nuevo.
   */
  singleSave?: boolean;
}

type Version = { url: string; path?: string };

export const EditarVideoIA: React.FC<EditarVideoIAProps> = ({
  clipUrl,
  originalPath,
  durationSec,
  aspectRatio,
  projectId,
  companyId,
  onSave,
  onClose,
  showOriginalPreview = false,
  replaceHint = 'Reemplaza el video en la timeline',
  copyHint = 'Se suma a Archivos, conserva el original',
  singleSave = false,
}) => {
  const [instruction, setInstruction] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEditSuccess, setShowEditSuccess] = useState(false);
  const [history, setHistory] = useState<Version[]>([
    { url: clipUrl, path: originalPath },
  ]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveMenu, setShowSaveMenu] = useState(false);
  const [showInsufficient, setShowInsufficient] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const credits = creditsForOmniEdit(durationSec);
  const hasEdits = currentIndex > 0;
  const currentUrl = history[currentIndex]?.url || clipUrl;
  // Mostramos un <video> real cuando ya hay una edición, o cuando el contenedor
  // pidió previsualizar el original (historial). Si no, placeholder en blanco.
  const showVideo = hasEdits || (showOriginalPreview && !!currentUrl);
  const creditLabel =
    credits === 1 ? 'Se consumirá 1 crédito' : `Se consumirán ${credits} créditos`;

  // Reset al cambiar de clip (el panel se re-monta con otro clipUrl).
  useEffect(() => {
    setHistory([{ url: clipUrl, path: originalPath }]);
    setCurrentIndex(0);
    setInstruction('');
    setError(null);
    setShowEditSuccess(false);
    setShowSaveMenu(false);
  }, [clipUrl, originalPath]);

  useEffect(() => {
    if (!showEditSuccess) return;
    const t = window.setTimeout(() => setShowEditSuccess(false), 3000);
    return () => window.clearTimeout(t);
  }, [showEditSuccess]);

  const checkCredits = async (): Promise<boolean> => {
    try {
      const { data: cp } = await supabase
        .from('company_profiles')
        .select('imagenes_restantes')
        .eq('id_company', companyId)
        .single();
      const rem = (cp as any)?.imagenes_restantes as number | undefined;
      if (typeof rem === 'number' && rem < credits) {
        setRemaining(rem);
        setShowInsufficient(true);
        return false;
      }
      return true;
    } catch {
      return true;
    }
  };

  const handleSend = async () => {
    const trimmed = instruction.trim();
    if (!trimmed || isSubmitting) return;
    if (!(await checkCredits())) return;
    setIsSubmitting(true);
    setError(null);
    setShowEditSuccess(false);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('No se pudo identificar al usuario.');
      const result = await omniEditVideo({
        userId: user.id,
        companyId,
        projectId,
        clipUrl: currentUrl,
        instruction: trimmed,
        aspectRatio,
        durationSec,
      });
      setHistory((prev) => {
        const base = prev.slice(0, currentIndex + 1);
        return [...base, { url: result.signedUrl, path: result.path }];
      });
      setCurrentIndex((prev) => prev + 1);
      setInstruction('');
      setShowEditSuccess(true);
    } catch (e: any) {
      setError(e?.message || 'Ocurrió un error al editar el video.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUndo = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };
  const handleRedo = () => {
    if (currentIndex < history.length - 1) setCurrentIndex(currentIndex + 1);
  };

  const doSave = async (mode: 'replace' | 'copy') => {
    const cur = history[currentIndex];
    if (!cur?.path) {
      setError('No se pudo guardar: el video editado no está listo.');
      return;
    }
    setIsSaving(true);
    setShowSaveMenu(false);
    try {
      await onSave({ url: cur.url, path: cur.path }, mode);
      onClose();
    } catch (e: any) {
      setError(e?.message || 'No se pudo guardar el video.');
    } finally {
      setIsSaving(false);
    }
  };

  const autoResize = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
    el.style.overflowY = el.scrollHeight > 132 ? 'auto' : 'hidden';
  };

  return (
    <div className="flex flex-col h-full p-3 gap-3">
      {/* ── ARRIBA: el video (versión actual / editada), grande, llena el
      espacio disponible del panel ─────────────────────────────────────────── */}
      <div
        className={`flex-1 min-h-[180px] relative rounded-xl border border-black/[0.06] overflow-hidden flex items-center justify-center ${
          showVideo ? 'bg-black' : 'bg-gray-50'
        }`}
      >
        {(isSubmitting || showEditSuccess) && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full shadow-sm">
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                  <span className="text-[13px] font-medium text-gray-700">
                    Editando video…
                  </span>
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-[13px] font-medium text-gray-700">
                    Video editado
                  </span>
                </>
              )}
            </div>
          </div>
        )}
        {showVideo ? (
          // Versión actual (original en el historial, o editada) → la mostramos.
          <video
            key={currentUrl}
            src={currentUrl}
            controls
            playsInline
            className="max-w-full max-h-full object-contain"
          >
            Tu navegador no soporta la reproducción de video.
          </video>
        ) : (
          // Sin editar todavía → espacio en blanco con placeholder. El video
          // editado aparece acá al generar (como el flujo de editar imagen).
          <div className="flex flex-col items-center gap-2 text-center px-6">
            <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-blue-500" />
            </div>
            <p className="text-sm font-medium text-gray-700">
              El video editado va a aparecer acá
            </p>
            <p className="text-xs text-gray-500">
              Describí el cambio abajo y Atlas lo aplica.
            </p>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 px-3 py-2 rounded-md shrink-0">
          <p className="text-[13px] font-medium text-red-600">
            No se pudo editar
          </p>
          <p className="text-[13px] text-red-600 mt-0.5">{error}</p>
        </div>
      )}

      {/* ── ABAJO: composer (prompt + créditos + botones) ──────────────────── */}
      <div className="flex flex-col gap-2 shrink-0">
        <div
          className={`flex flex-col bg-gray-50 border border-gray-300 rounded-3xl ${
            isSubmitting ? 'shine-effect' : ''
          } focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500`}
        >
          <div className="relative min-h-[44px]">
            <textarea
              ref={inputRef}
              value={instruction}
              onChange={(e) => {
                setInstruction(e.target.value);
                autoResize(e.target);
              }}
              placeholder="Describí el cambio que querés hacer…"
              disabled={isSubmitting}
              rows={1}
              className="w-full pl-10 pr-4 bg-transparent focus:outline-none border-none text-sm placeholder:text-gray-400 disabled:text-gray-500 disabled:cursor-not-allowed resize-none overflow-hidden whitespace-pre-wrap"
              style={{
                lineHeight: '1.25rem',
                paddingTop: '0.75rem',
                paddingBottom: '0.5rem',
              }}
            />
            <span className="pointer-events-none absolute left-3 top-3 text-gray-400">
              <Sparkles className="w-4 h-4" aria-hidden="true" />
            </span>
          </div>
          <div className="flex justify-end px-2 pb-2">
            <button
              type="button"
              onClick={handleSend}
              disabled={isSubmitting || !instruction.trim()}
              className="h-9 w-9 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white rounded-full font-normal shadow-sm inline-flex items-center justify-center"
              aria-label="Enviar instrucción"
            >
              <ArrowUp className="w-4 h-4" />
            </button>
          </div>
        </div>

        <p className="text-xs text-gray-900 font-medium flex items-center gap-1.5">
          <Info className="w-4 h-4 text-blue-500 flex-shrink-0" />
          {creditLabel}.
        </p>
        <p className="text-xs text-gray-500 -mt-1">
          Pedí cambios genéricos (estilo, fondo, color). Los detalles muy chicos
          pueden no aplicarse bien. Máx. 10 segundos.
        </p>

        {/* Deshacer / Rehacer / Guardar — solo tras ≥1 edición */}
        {history.length > 1 && (
          <div className="flex items-center gap-2 mt-1">
            <button
              type="button"
              onClick={handleUndo}
              disabled={currentIndex <= 0 || isSaving}
              className="flex-1 h-10 px-2 bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9] disabled:opacity-60 disabled:cursor-not-allowed rounded-full font-normal shadow-sm inline-flex items-center justify-center gap-1.5"
            >
              <Undo2 className="w-4 h-4" />
              <span className="text-[13px]">Deshacer</span>
            </button>
            <button
              type="button"
              onClick={handleRedo}
              disabled={currentIndex >= history.length - 1 || isSaving}
              className="flex-1 h-10 px-2 bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9] disabled:opacity-60 disabled:cursor-not-allowed rounded-full font-normal shadow-sm inline-flex items-center justify-center gap-1.5"
            >
              <Redo2 className="w-4 h-4" />
              <span className="text-[13px]">Rehacer</span>
            </button>

            {singleSave ? (
              // Historial: un solo botón "Guardar" — siempre guarda como item
              // NUEVO (nunca reemplaza ni borra el video original).
              <button
                type="button"
                onClick={() => doSave('copy')}
                disabled={isSaving || !hasEdits}
                className="flex-1 h-10 px-2 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed text-white rounded-full font-normal shadow-sm inline-flex items-center justify-center gap-1.5"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                <span className="text-[13px]">Guardar</span>
              </button>
            ) : (
              /* Split-button Guardar / Guardar como copia (timeline) */
              <div className="relative flex-1">
                <div className="flex">
                  <button
                    type="button"
                    onClick={() => doSave('replace')}
                    disabled={isSaving || !hasEdits}
                    className="flex-1 h-10 pl-2 pr-1.5 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed text-white rounded-l-full font-normal shadow-sm inline-flex items-center justify-center gap-1.5"
                  >
                    {isSaving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    <span className="text-[13px]">Guardar</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowSaveMenu((v) => !v)}
                    disabled={isSaving || !hasEdits}
                    className="h-10 px-1.5 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white rounded-r-full border-l border-blue-400 inline-flex items-center justify-center"
                    aria-label="Más opciones de guardado"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
                {showSaveMenu && (
                  <div className="absolute right-0 bottom-full mb-2 w-56 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-30">
                    <button
                      type="button"
                      onClick={() => doSave('replace')}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-start gap-3"
                    >
                      <Save className="w-4 h-4 text-gray-600 mt-0.5" />
                      <span>
                        <span className="block text-sm font-medium text-gray-900">
                          Guardar
                        </span>
                        <span className="block text-xs text-gray-500">
                          {replaceHint}
                        </span>
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => doSave('copy')}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-start gap-3 border-t border-gray-100"
                    >
                      <Copy className="w-4 h-4 text-gray-600 mt-0.5" />
                      <span>
                        <span className="block text-sm font-medium text-gray-900">
                          Guardar como copia
                        </span>
                        <span className="block text-xs text-gray-500">
                          {copyHint}
                        </span>
                      </span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de créditos insuficientes (overlay) */}
      {showInsufficient &&
        createPortal(
          <div className="fixed inset-0 bg-brand-black/30 z-[80] flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-sm rounded-2xl shadow-lg p-6 relative text-center">
              <button
                type="button"
                onClick={() => setShowInsufficient(false)}
                className="absolute top-3 right-3 p-2 text-gray-500 hover:text-gray-700"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-100 flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Créditos insuficientes
              </h3>
              <p className="text-sm text-gray-600 mb-5">
                Para editar este video necesitás{' '}
                <span className="font-semibold">{credits} créditos</span>, pero
                solo tenés{' '}
                <span className="font-semibold">{remaining} disponibles</span>.
              </p>
              <button
                type="button"
                onClick={() => setShowInsufficient(false)}
                className="inline-flex items-center justify-center w-full h-10 bg-blue-500 hover:bg-blue-600 text-white rounded-full text-sm font-medium"
              >
                Entendido
              </button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default EditarVideoIA;
